<template>
  <div class="home view">
    <Header
      navbar-type="custom"
      :show-back-btn="false"
      :screen-title="t('CL_TRANSACTIONS.FILTERS_TITLE')"
    >
      <div class="col-3">
        <div id="close-btn" @click="onClose" class="px-0">
          <i
            class="fa fa-times pe-0 ms-3 me-0"
            style="font-size: x-large !important"
          ></i>
        </div>
      </div>
      <hr class="mt-3 mb-0 p-0" />
    </Header>
    <section id="filters-container" class="px-3">
      <CollapseCard
        fill
        :title="t('CL_TRANSACTIONS.DATE_FILTER_TITLE')"
        rtf-title
        :content-style="collapseStyle"
        :default-open="!!activeDateFilter"
      >
        <div
          class="d-flex flex-column w-100 gap-3 btn-group"
          data-toggle="buttons"
        >
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/week_icon.svg')"
            :text="t('CL_TRANSACTIONS.LAST_WEEK')"
            name="last_week"
            v-model:value="dateFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/calendar_month.svg')"
            :text="t('CL_TRANSACTIONS.LAST_MONTH')"
            name="last_month"
            v-model:value="dateFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/edit_calendar.svg')"
            :text="dateRangeText"
            @click="showCalendar = true"
            name="custom_range"
            v-model:value="dateFilterValue"
          />
          <VueDatePicker
            v-if="showCalendar"
            inline
            range
            class="w-100"
            ref="VueDatePickerRef"
            show-now-button
            @range-end="disableCustomDateBtn = false"
            :enable-time-picker="false"
            v-model="customDateRange"
            :placeholder="t('CL_TRANSACTIONS.LAST_MONTH')"
            menu-class-name="cal-menu"
          >
            <template #action-row>
              <div class="action-row w-100">
                <div class="action-row w-100">
                  <Button
                    class="select-button"
                    @click="selectDate"
                    :disabled="disableCustomDateBtn"
                  >
                    {{ t("CL_TRANSACTIONS.RANGE_BTN") }}
                  </Button>
                </div>
              </div>
            </template>
          </VueDatePicker>
        </div>
      </CollapseCard>
      <CollapseCard
        fill
        :title="t('CL_TRANSACTIONS.CATEGORY_FILTER_TITLE')"
        rtf-title
        :content-style="collapseStyle"
        :default-open="!!activeCategoryFilter"
      >
        <div
          class="d-flex flex-column w-100 gap-3 btn-group"
          data-toggle="buttons"
        >
          <h3 class="fs-6 mb-0 text-black">
            {{ t("CL_TRANSACTIONS.MONEY_OUT") }}
          </h3>
          <hr class="m-0 p-0" />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/credit_card.svg')"
            name="card_transactions"
            :text="t('CL_TRANSACTIONS.CARD_TRANSACTIONS')"
            v-model:value="categoryFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/payments.svg')"
            name="cash_withdrawals"
            :text="t('CL_TRANSACTIONS.CASH_WITHDRAWALS')"
            v-model:value="categoryFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/wallet.svg')"
            name="online_transfer"
            :text="t('CL_TRANSACTIONS.ONLINE_TRANSFER')"
            v-model:value="categoryFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/phone_iphone.svg')"
            name="phone_purchase"
            :text="t('CL_TRANSACTIONS.PHONE_PURCHASE')"
            v-model:value="categoryFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/warning.svg')"
            name="declined_transactions"
            :text="t('CL_TRANSACTIONS.DECLINED_TRANSACTIONS')"
            v-model:value="categoryFilterValue"
          />
          <h3 class="fs-6 mb-0 text-black mt-3">
            {{ t("CL_TRANSACTIONS.MONEY_IN") }}
          </h3>
          <hr class="m-0 p-0" />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/account_balance.svg')"
            name="payments"
            :text="t('CL_TRANSACTIONS.PAYMENTS')"
            v-model:value="categoryFilterValue"
          />
          <TransactionFilterOption
            :icon="useAsset('img/credit-line/keyboard_return.svg')"
            name="refunds"
            :text="t('CL_TRANSACTIONS.REFUNDS')"
            v-model:value="categoryFilterValue"
          />
        </div>
      </CollapseCard>
    </section>

    <Button
      id="submit-btn"
      @click="onApplyFilters"
      :disabled="disableApplyFiltersBtn"
    >
      <div class="text-white text-center my-auto fw-bold btn-text">
        <span>{{ t("CL_TRANSACTIONS.BTN_TEXT") }}</span>
      </div>
    </Button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import Header from "@/shared-components/consumer/Header.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import CollapseCard from "@/apps/credit-line/components/CollapseCard.vue";
import TransactionFilterOption from "@/apps/credit-line/components/Transactions/TransanctionFilterOption.vue";
import TransactionFilter from "@/apps/credit-line/types/TransactionFilter";
import VueDatePicker from "@vuepic/vue-datepicker";
import type { DatePickerInstance } from "@vuepic/vue-datepicker";
import { useStore } from "vuex";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";
export default defineComponent({
  name: "TransactionFilters",
  components: {
    VueDatePicker,
    TransactionFilterOption,
    CollapseCard,
    Button,
    Header,
  },
  methods: {
    useAsset(path: string): string {
      return useAsset(path);
    },
  },
  data() {
    return {
      collapseStyle: {
        background: "white",
        borderBottom: "1px solid #F2F2F2",
        borderRadius: "0 0 10px 10px",
      },
    };
  },
  emits: ["closeFiltersView", "update:dateFilter", "update:categoryFilter"],
  setup(_, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const dateFilterValue = ref<TransactionFilter>();
    const categoryFilterValue = ref<TransactionFilter>();
    const showCalendar = ref<boolean>(false);
    const customDateRange = ref<Date[]>([]);
    const dateRangeText = ref<string>(t("CL_TRANSACTIONS.SELECT_DATE_RANGE"));
    const disableCustomDateBtn = ref<boolean>(true);
    const VueDatePickerRef = ref<DatePickerInstance>(null);

    const activeDateFilter = computed(
      (): TransactionFilter =>
        (store.state.creditLine.transactionsFilters &&
          store.state.creditLine.transactionsFilters?.find(
            (x: TransactionFilter) => x.type === "date"
          )) ??
        null
    );
    const activeCategoryFilter = computed(
      (): TransactionFilter =>
        (store.state.creditLine.transactionsFilters &&
          store.state.creditLine.transactionsFilters.find(
            (x: TransactionFilter) => x.type === "category"
          )) ??
        null
    );

    const disableApplyFiltersBtn = computed((): boolean => {
      let selectedRange = false;
      if (dateFilterValue.value?.id === "custom_range") {
        selectedRange =
          !(customDateRange.value.length > 0) && showCalendar.value === true;
      }
      return (
        !(!!dateFilterValue.value || !!categoryFilterValue.value) ||
        selectedRange
      );
    });

    onMounted(() => {
      dateFilterValue.value = activeDateFilter.value;
      categoryFilterValue.value = activeCategoryFilter.value;
      if (
        activeDateFilter.value &&
        activeDateFilter.value.range &&
        activeDateFilter.value.text
      ) {
        dateRangeText.value = activeDateFilter.value.text;
      }
    });

    watch(dateFilterValue, (newVal) => {
      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CreditLine.ClickFilterDate,
        "Transactions"
      );
      if (newVal?.id !== "custom_range") {
        showCalendar.value = false;
        dateRangeText.value = t("CL_TRANSACTIONS.SELECT_DATE_RANGE");
      }
    });

    watch(categoryFilterValue, () =>
      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CreditLine.ClickFilterCategory,
        "Transactions"
      )
    );

    const formatDateRange = (date: Date) => {
      const newDate = new Date(date)
        .toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
        })
        .toLowerCase()
        .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

      if (newDate?.split(" ")[1]) {
        return `${newDate?.split(" ")[1]} ${newDate?.split(" ")[0]}`;
      }

      return `${newDate?.split("-")[1]} ${newDate?.split("-")[0]}`;
    };

    const selectDate = () => {
      VueDatePickerRef?.value?.selectDate();
      if (customDateRange.value) {
        dateRangeText.value = `${formatDateRange(
          customDateRange.value[0] as Date
        )} - ${formatDateRange(customDateRange.value[1] as Date)}`;
      }
      showCalendar.value = false;
    };

    const onClose = () => emit("closeFiltersView");

    const applyDateFilter = (
      dateFilter: TransactionFilter | undefined
    ): TransactionFilter => {
      return { type: "date", ...dateFilter } as TransactionFilter;
    };

    const applyCategoryFilter = (
      categoryFilter: TransactionFilter | undefined
    ): TransactionFilter => {
      return { type: "category", ...categoryFilter } as TransactionFilter;
    };

    const onApplyFilters = () => {
      let newTransactionsFilters: TransactionFilter[] = [];
      if (dateFilterValue.value) {
        if (dateFilterValue.value?.id === "custom_range") {
          if (
            activeDateFilter.value &&
            activeDateFilter.value.range &&
            (!customDateRange.value[0] || !customDateRange.value[1])
          ) {
            newTransactionsFilters.push(activeDateFilter.value);
          } else {
            newTransactionsFilters.push(
              applyDateFilter({
                ...dateFilterValue.value,
                text: dateRangeText.value,
                range: [
                  customDateRange.value[0] as Date,
                  customDateRange.value[1] as Date,
                ],
              })
            );
          }
        } else {
          newTransactionsFilters.push(applyDateFilter(dateFilterValue.value));
        }
      }

      if (categoryFilterValue.value) {
        newTransactionsFilters.push(
          applyCategoryFilter(categoryFilterValue.value)
        );
      }

      store.dispatch(
        "creditLine/setTransactionsFilters",
        newTransactionsFilters
      );
      emit("closeFiltersView");
    };

    return {
      t,
      showCalendar,
      dateRangeText,
      customDateRange,
      dateFilterValue,
      VueDatePickerRef,
      activeDateFilter,
      categoryFilterValue,
      activeCategoryFilter,
      disableCustomDateBtn,
      disableApplyFiltersBtn,
      onClose,
      selectDate,
      onApplyFilters,
      formatDateRange,
    };
  },
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.view {
  padding-bottom: 20%;
}
#close-btn {
  margin-right: 0 !important;
  margin-left: 40% !important;
  background: transparent;
  box-shadow: none;
  border: none;
}

#submit-btn {
  position: fixed;
  bottom: 2%;
  left: 0;
  right: 0;
  display: flex;
  width: 90%;
  padding: 12px 16px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 44px;
  border-radius: 10px;
  margin: 0 auto;
}

.cal-menu {
  border-radius: 8px;
  border: 1px solid var(--borders-dark, #ddd);
}

.select-button {
  height: 44px;
  padding: 10px 24px;
  width: 100%;
  border-radius: 10px;
  background: $pj-color-green;
  margin-left: auto;
  margin-right: auto;
}
</style>
<style>
.dp__theme_light {
  --dp-primary-color: #2c7040 !important;
}
</style>
